import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { onboardingApi } from '@/api/endpoints'
import { Button, EnterChip } from '@/components/ui/Button'
import { RadioCard, YesNo } from '@/components/ui/Toggle'
import { useCompleteOnboarding, useEnterToContinue, useSaveStep } from '../useWizard'
import { ChoiceWrap, QuestionRow, WizardActions, WizardPanel } from '../WizardShell'
import type { ResumeOptimization } from '@/types/api'

const OPTIMIZATION_OPTIONS: Array<{
  value: ResumeOptimization
  title: string
  description: string
}> = [
  { value: 'off', title: 'Off', description: 'Send your resume exactly as uploaded.' },
  {
    value: 'honest',
    title: 'Honest',
    description: "Reorder and emphasize experience that's relevant to each job.",
  },
  {
    value: 'aggressive',
    title: 'Aggressive',
    description: 'Rewrite content to match the job description closely.',
  },
]

export function SettingsStep() {
  const save = useSaveStep('application_settings')
  const complete = useCompleteOnboarding()

  const { data: existing } = useQuery({
    queryKey: ['application-settings'],
    queryFn: onboardingApi.getSettings,
    staleTime: 30_000,
  })

  const [optimization, setOptimization] = useState<ResumeOptimization>('honest')
  const [autoApprove, setAutoApprove] = useState(true)
  const [reviewBeforeSubmit, setReviewBeforeSubmit] = useState(false)
  const [hydrated, setHydrated] = useState(false)

  // Seed once from the server, then let the user's edits win.
  if (existing && !hydrated) {
    setOptimization(existing.resumeOptimization)
    setAutoApprove(existing.autoApproveEdits)
    setReviewBeforeSubmit(existing.reviewBeforeSubmit)
    setHydrated(true)
  }

  const busy = save.isPending || complete.isPending

  const finish = () => {
    if (busy) return
    save.mutate(
      {
        resumeOptimization: optimization,
        autoApproveEdits: autoApprove,
        generateCoverLetter: true,
        reviewBeforeSubmit,
      },
      { onSuccess: () => complete.mutate() },
    )
  }
  useEnterToContinue(finish)

  return (
    <>
      <div className="space-y-5">
        <WizardPanel title="Resume optimization">
          <div role="radiogroup" aria-label="Resume optimization">
            {OPTIMIZATION_OPTIONS.map((option, index) => (
              <RadioCard
                key={option.value}
                selected={optimization === option.value}
                onSelect={() => setOptimization(option.value)}
                title={option.title}
                description={option.description}
                className={index > 0 ? 'border-t border-line' : ''}
              />
            ))}
          </div>
          <QuestionRow
            label="Auto-approve edits?"
            hint="Skip the preview step and send optimized files straight through."
            className="border-t border-line"
            last
          >
            <ChoiceWrap>
              <YesNo value={autoApprove} onChange={setAutoApprove} aria-label="Auto-approve edits" />
            </ChoiceWrap>
          </QuestionRow>
        </WizardPanel>

        <WizardPanel title="Cover letter" bodyClassName="px-4 py-4 sm:px-5">
          <p className="text-[14.5px] leading-relaxed text-ink-soft">
            Generated automatically when a job asks for one. Tailored to the role from your profile
            (and your saved cover letter, if you have one). No setup needed.
          </p>
        </WizardPanel>

        <WizardPanel title="Review before submit">
          <div role="radiogroup" aria-label="Review before submit">
            <RadioCard
              selected={reviewBeforeSubmit}
              onSelect={() => setReviewBeforeSubmit(true)}
              title="On"
              description="Pause on a review screen so you can check and edit each application before it's submitted."
            />
            <RadioCard
              selected={!reviewBeforeSubmit}
              onSelect={() => setReviewBeforeSubmit(false)}
              title="Off"
              description="Submit applications automatically once they're filled."
              className="border-t border-line"
            />
          </div>
        </WizardPanel>
      </div>

      <WizardActions
        aside={
          <span className="inline-flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" />
            Autosaved
          </span>
        }
      >
        <Button size="lg" className="flex-1 sm:flex-none" onClick={finish} loading={busy}>
          Finish setup
          <EnterChip />
        </Button>
      </WizardActions>
    </>
  )
}
